import { PocIcon } from 'commons/components/components-wrapper/poc-icon.component';
import { PocTooltip } from 'commons/components/components-wrapper/poc-tooltip.component';
import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { useAsyncTooltipMount } from 'commons/hooks';
import { useUserPreferencesContext } from 'core/contexts/user-preferences/hooks/use-user-preferences-context.hook';
import { useTranslation } from 'core/i18n/i18n';
import { formatDateTimeOrNow } from 'core/utils/dates';
import { ConnectionStatus } from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/types/active-directory.type';
import { ActiveDirectoryStatusBaseContainer } from './active-directory-status-base.style';

interface ActiveDirectoryStatusBaseProps {
    status: ConnectionStatus;
    iconName: React.ComponentProps<typeof PocIcon>['name'];
    iconStatus?: React.ComponentProps<typeof PocIcon>['status'];
    label: string;
    errorMessage?: string;
    lastAttempt?: string;
}

export const ActiveDirectoryStatusBase: React.FC<ActiveDirectoryStatusBaseProps> = ({
    status,
    iconName,
    iconStatus,
    label,
    errorMessage,
    lastAttempt,
}) => {
    const { t } = useTranslation();
    const { userLanguage } = useUserPreferencesContext();
    const { tooltipAnchorId, isMountedTooltip, isVisibleTooltip, onMouseEnter, onMouseLeave } = useAsyncTooltipMount({
        mountDelayMs: 250,
    });

    return (
        <div data-testid={`active-directory-${status}`}>
            <ActiveDirectoryStatusBaseContainer
                id={tooltipAnchorId}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                <PocIcon data-testid="connection-status-icon" name={iconName} family="outlined" status={iconStatus} />
                <PocTypography element="p" variant="body2">
                    {label}
                </PocTypography>
            </ActiveDirectoryStatusBaseContainer>
            {isMountedTooltip && errorMessage && (
                <PocTooltip
                    data-testid="connection-status-tooltip"
                    anchor={tooltipAnchorId}
                    placement="top"
                    disableTriggers
                    visible={isVisibleTooltip}
                >
                    {errorMessage}
                    {lastAttempt && (
                        <>
                            <br />
                            {t(`settings.integrations.active-directory.host.info.connection.lastAttempt`, {
                                lastAttempt: formatDateTimeOrNow(
                                    lastAttempt,
                                    userLanguage,
                                    t('commons.utils.date.now')
                                ),
                            })}
                        </>
                    )}
                </PocTooltip>
            )}
        </div>
    );
};
